/**
 * Admin service for system management operations
 */

import { apiClient } from './api';
import type { User, UserUpdate } from '../types';

// Admin dashboard interfaces
export interface DashboardStats {
  users: {
    admin_users: number;
    instructor_users: number;
    learner_users: number;
    total_users: number;
    active_users: number;
  };
  runs: {
    pending_runs: number;
    provisioning_runs: number;
    active_runs: number;
    destroying_runs: number;
    destroyed_runs: number;
    failed_runs: number;
    total_runs: number;
  };
  labs: {
    total_labs: number;
    labs_by_difficulty: Record<string, number>;
  };
  active_runs: number;
  recent: {
    new_users_24h: number;
    new_runs_24h: number;
    new_grades_24h: number;
    grade_overrides_24h: number;
  };
  timestamp: string;
}

export interface ActivityItem {
  id: number;
  action: string;
  resource_type: string;
  resource_id: string;
  details: Record<string, any>;
  created_at: string;
  user: {
    id: number;
    username: string;
    role: string;
  } | null;
  ip_address: string | null;
}

export interface SystemHealth {
  database: {
    status: string;
    details: string | null;
  };
  redis: {
    status: string;
    details: string;
  };
  digitalocean: {
    status: string;
    details: string;
  };
  background_workers: {
    status: string;
    details: string;
  };
  celery_beat?: {
    status: string;
    details: string;
  };
  overall: string;
  timestamp: string;
}

// User management interfaces
export interface AdminUserListResponse {
  users: AdminUser[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  cohort?: string | null;
  assigned_instructor_id?: number | null;
  created_at: string;
  updated_at: string;
  run_count: number;
  active_run_count: number;
  last_login: string | null;
}

export interface BulkActionResult {
  action: string;
  results: {
    success: number[];
    errors: Array<{
      user_id: number;
      error: string;
    }>;
  };
  total_requested: number;
  successful: number;
  failed: number;
}

export type AdminUserProfileUpdate = Pick<
  UserUpdate,
  'first_name' | 'last_name' | 'cohort' | 'assigned_instructor_id'
>;

// Lab analytics interface
export interface LabAnalytics {
  period_days: number;
  start_date: string;
  total_runs: number;
  recent_runs: number;
  state_distribution: Record<string, number>;
  completion_rate: number;
  lab_analytics: Record<string, {
    title: string;
    difficulty: string;
    total_runs: number;
    recent_runs: number;
    estimated_minutes: number;
  }>;
  timestamp: string;
}

// System monitoring interfaces
export interface SystemMetrics {
  period_hours: number;
  start_time: string;
  user_registrations: number;
  new_runs: number;
  active_runs: number;
  new_grades: number;
  grade_overrides: number;
  error_events: number;
  timestamp: string;
}

export interface AuditLogsResponse {
  logs: AuditLogEntry[];
  total_count: number;
  period_hours: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AuditLogEntry {
  id: number;
  action: string;
  resource_type: string;
  resource_id: string;
  details: Record<string, any>;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
  user: {
    id: number;
    username: string;
    role: string;
  } | null;
}

// System configuration interface
export interface SystemSettingsResponse {
  settings: Record<string, {
    value: any;
    description: string;
    is_public: boolean;
    updated_at: string | null;
  }>;
  count: number;
  timestamp: string;
}

export interface SettingsUpdateResponse {
  updated_keys: string[];
  errors: Array<{
    key: string;
    error: string;
  }>;
  total_requested: number;
  successful: number;
  failed: number;
}

// Lab management interfaces
export interface AdminLab {
  id: number;
  slug: string;
  title: string;
  summary: string | null;
  os: string;
  droplet_size: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimated_minutes: number;
  tags: string[];
  prerequisites: string[];
  total_runs: number;
  active_runs: number;
  created_at: string;
  updated_at: string;
}

export interface AdminLabDetails extends AdminLab {
  manifest: Record<string, any>;
  usage_stats: {
    total_runs: number;
    recent_runs_30d: number;
    state_distribution: Record<string, number>;
    avg_completion_minutes: number | null;
  };
}

export interface AdminLabListResponse {
  labs: AdminLab[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface LabGradingCheck {
  id: string;
  description: string;
  points: number;
  check_type: string;
  check_config: Record<string, any>;
  command_example: string;
}

export interface LabGradingDetails {
  slug: string;
  title: string;
  difficulty: string;
  estimated_minutes: number;
  total_points: number;
  check_count: number;
  checks: LabGradingCheck[];
}

export class AdminService {
  // Dashboard endpoints
  static async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get('/admin/dashboard/stats');
    return response;
  }

  static async getRecentActivity(limit = 50, offset = 0): Promise<ActivityItem[]> {
    const response = await apiClient.get('/admin/dashboard/activity', {
      params: { limit, offset }
    });
    return response;
  }

  static async getSystemHealth(): Promise<SystemHealth> {
    const response = await apiClient.get('/admin/dashboard/health');
    return response;
  }

  // User management endpoints
  static async getUserList(params: {
    search?: string;
    role?: string;
    is_active?: boolean;
    created_after?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<AdminUserListResponse> {
    const response = await apiClient.get('/admin/users', { params });
    return response;
  }

  static async bulkUserAction(
    userIds: number[],
    action: string,
    newRole?: string,
    newStatus?: boolean
  ): Promise<BulkActionResult> {
    const params = new URLSearchParams({ action });
    if (newRole !== undefined) params.append('new_role', newRole);
    if (newStatus !== undefined) params.append('new_status', newStatus.toString());
    
    const response = await apiClient.post(
      `/admin/users/bulk-action?${params.toString()}`,
      userIds
    );
    return response;
  }

  static async getUserActivity(userId: number, limit = 50, offset = 0): Promise<ActivityItem[]> {
    const response = await apiClient.get(`/admin/users/${userId}/activity`, {
      params: { limit, offset }
    });
    return response;
  }

  static async generateTempPassword(
    userId: number,
    expiresMinutes?: number
  ): Promise<{ temp_password: string; expires_at: string | null }> {
    const response = await apiClient.post(`/admin/users/${userId}/temp-password`, {
      expires_minutes: expiresMinutes,
    });
    return response;
  }

  static async updateUserProfile(
    userId: number,
    data: AdminUserProfileUpdate
  ): Promise<User> {
    const response = await apiClient.put<User>(`/users/${userId}`, data);
    return response;
  }

  // Individual user action methods
  static async activateUser(userId: number): Promise<BulkActionResult> {
    const response = await apiClient.post(
      '/admin/users/bulk-action?action=activate',
      [userId]
    );
    return response;
  }

  static async deactivateUser(userId: number): Promise<BulkActionResult> {
    const response = await apiClient.post(
      '/admin/users/bulk-action?action=deactivate',
      [userId]
    );
    return response;
  }

  static async changeUserRole(userId: number, newRole: string): Promise<BulkActionResult> {
    const response = await apiClient.post(
      `/admin/users/bulk-action?action=change_role&new_role=${encodeURIComponent(newRole)}`,
      [userId]
    );
    return response;
  }

  static async deleteUser(userId: number): Promise<BulkActionResult> {
    const response = await apiClient.post(
      '/admin/users/bulk-action?action=delete',
      [userId]
    );
    return response;
  }

  // Lab management endpoints
  static async getLabList(params?: {
    search?: string;
    difficulty?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminLabListResponse> {
    const response = await apiClient.get('/admin/labs', params ? { params } : undefined);
    return response;
  }

  static async getLabDetails(labSlug: string): Promise<AdminLabDetails> {
    const response = await apiClient.get(`/admin/labs/${labSlug}`);
    return response;
  }

  static async createLab(labData: {
    slug: string;
    title: string;
    summary?: string;
    os: string;
    droplet_size?: string;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimated_minutes: number;
    tags?: string[];
    prerequisites?: string[];
    manifest: Record<string, any>;
  }): Promise<{ message: string; lab: { id: number; slug: string; title: string; difficulty: string } }> {
    const response = await apiClient.post('/admin/labs', labData);
    return response;
  }

  static async updateLab(labSlug: string, labData: Partial<{
    title: string;
    summary: string;
    os: string;
    droplet_size: string;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimated_minutes: number;
    tags: string[];
    prerequisites: string[];
    manifest: Record<string, any>;
  }>): Promise<{ message: string; lab: { id: number; slug: string; title: string; difficulty: string; updated_at: string } }> {
    const response = await apiClient.put(`/admin/labs/${labSlug}`, labData);
    return response;
  }

  static async deleteLab(labSlug: string, force = false): Promise<{ message: string; active_runs_affected: number }> {
    const url = `/admin/labs/${labSlug}${force ? '?force=true' : ''}`;
    const response = await apiClient.delete(url);
    return response;
  }

  static async getLabAnalytics(labSlug?: string, days = 30): Promise<LabAnalytics> {
    const response = await apiClient.get('/admin/labs/analytics', {
      params: { lab_slug: labSlug, days }
    });
    return response;
  }

  static async getLabGradingDetails(): Promise<LabGradingDetails[]> {
    const response = await apiClient.get('/labs/grading-details');
    return response;
  }

  // System monitoring endpoints
  static async getSystemMetrics(hours = 24): Promise<SystemMetrics> {
    const response = await apiClient.get('/admin/monitoring/metrics', {
      params: { hours }
    });
    return response;
  }

  static async getAuditLogs(params: {
    action?: string;
    resource_type?: string;
    user_id?: number;
    hours?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<AuditLogsResponse> {
    const response = await apiClient.get('/admin/monitoring/audit-logs', { params });
    return response;
  }

  // Configuration management endpoints
  static async getSystemSettings(includePrivate = true): Promise<SystemSettingsResponse> {
    const response = await apiClient.get('/admin/settings', {
      params: { include_private: includePrivate }
    });
    return response;
  }

  static async updateSystemSettings(settings: Record<string, any>): Promise<SettingsUpdateResponse> {
    const response = await apiClient.put('/admin/settings', settings);
    return response;
  }
}
